// =============================================================================
// D4 — Grille Prospects (staging / quarantaine) — Hub Commercial
// =============================================================================
// Réf : docs/audits/PLAN-IMPL-D4.md, LOT 1. Aligné sur la grille de référence
// HubGrid.jsx (OHGrid standard : tri, filtres, recherche, pagination, sélection
// multiple, actions de ligne, clic-ligne → détail).
//
// GÉNÉRIQUE : colonnes dérivées du profil (OMV = CEE). La CONVERSION en lot
// arrive au LOT 2 (vrai geste de Solène). Le bouton "+ Prospect (test)" est un
// échafaudage de validation interne (vrai flux = formulaire public LOT 3).
// =============================================================================
import { useState, useEffect, useCallback, useMemo } from "react";
import { Inbox, Plus, Trash2, XCircle, Copy, Building2, Hash, Archive, ArchiveRestore, Eye, Download, UserCheck } from "lucide-react";
import { OHGrid } from "../../components/ui/oh-grid";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
// Le formulaire prospect RÉUTILISE le composant du formulaire DOSSIER (mêmes champs,
// mêmes dropdowns config-list, même structure 2 parties) — pas de formulaire parallèle.
import OmvCeeEditor from "../../components/omv/OmvCeeEditor";
import ProspectCreationRapide from "./regie/ProspectCreationRapide";   // T-201 — fiche courte, pour les tenants qui la déclarent
import { usePermissions } from "../../contexts/PermissionContext";
import ClientDuplicateDialog from "./dossier/ClientDuplicateDialog";
import { enteteTenant } from "../../utils/enteteTenant";
import { telechargerCsv, optionsCommerciaux, resumeLot } from "./prospectsGrilleExport";   // T-202 — export CSV, filtre commercial, rapport de lot
import SuiviActions from "../../components/crm/SuiviActions";   // T-203 — notes datées et prochaine action
import MonLienCommercial from "./regie/MonLienCommercial";          // 🔴 ETI-82 — le commercial copie SON lien
import FicheProspectRegie from "./regie/FicheProspectRegie";        // 🔴 ETI-82 — suivi dossier read-only
import PorteDecision from "../../components/metier/portes/PorteDecision"; // 🔴 ETI-101 — porte du prospect
import EnvoiPartenaire from "../../components/metier/transfert/EnvoiPartenaire"; // ETI-115 L6 — envoi au partenaire

// Base API (retiré par erreur lors du refactor des imports e81a51769 → toutes les
// requêtes `${API}/…` levaient un ReferenceError capté en « Erreur réseau » + 0
// prospect, AUCUN appel réseau émis ; build OK car no-undef = simple warning).
const API = process.env.REACT_APP_BACKEND_URL;

// Libellés + couleurs de statut (retirés par erreur lors du refactor des imports
// e81a51769, comme `API` → StatutBadge levait un ReferenceError = PAGE BLANCHE dès
// qu'une ligne s'affichait, pire pour Solène/10 prospects).
const STATUT_LABELS = {
  nouveau: "Nouveau",
  en_revue: "En revue",
  converti: "Converti",
  rejete: "Rejeté",
  doublon: "Doublon",
  transmis: "Transmis",      // ETI-115 L1 — envoyé à un partenaire
};
const STATUT_COLOR = {
  nouveau: "blue", en_revue: "amber", converti: "green", rejete: "red", doublon: "orange",
  transmis: "purple",        // paire --oh-stat-purple-* (clair ET foncé)
};

// Libellé d'affichage de la SOURCE. Sources connues mappées (accents propres) ;
// pour une régie (source = son nom dérivé, ex "etincelle") → capitalisation par
// défaut → "Etincelle". "etincelle" explicitement accentué "Étincelle".
const SOURCE_LABELS = {
  manuel: "Manuel",
  import_ocr: "Import OCR",
  regie: "Régie",
  etincelle: "Étincelle",
  form_public_d4: "Formulaire web",
  site_simulateur: "Site – simulateur",
  site_contact: "Site – contact",
};
function sourceLabel(v) {
  if (!v) return "—";
  return SOURCE_LABELS[v] || (v.charAt(0).toUpperCase() + v.slice(1));
}


function formatDate(d) {
  if (!d) return "";
  let s = d;
  if (typeof s === "string" && !s.endsWith("Z") && !s.slice(-6).match(/[+-]\d\d:\d\d/)) s = `${s}Z`;
  const dt = new Date(s);
  return Number.isNaN(dt.getTime()) ? "" : dt.toLocaleDateString("fr-FR");
}

function StatutBadge({ value }) {
  const c = STATUT_COLOR[value] || "blue";
  return (
    <Badge style={{
      background: `var(--oh-stat-${c}-bg)`, color: `var(--oh-stat-${c}-text)`,
      border: `1px solid var(--oh-stat-${c}-border)`,
    }}>{STATUT_LABELS[value] || value}</Badge>
  );
}

export default function ProspectsStagingGrid({ tenantId, ouvrirProspectId = null, onProspectOuvert }) {
  const [data, setData] = useState({ prospects: [], total: 0, counts_by_statut: {}, commerciaux: {} });
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  // Rôle courant (depuis le user persisté) : la RÉGIE a une vue cloisonnée —
  // création + consultation de SES prospects, sans conversion ni cycle de vie
  // (le verrou réel est BACKEND : allowlist + filtre created_by). Solène = complet.
  const role = (() => { try { return (JSON.parse(localStorage.getItem("user") || "{}") || {}).role; } catch { return null; } })();
  const isRegie = role === "regie";
  // T-201 — fiche courte DÉCLARÉE par le tenant (lecture EXACTE : un super-admin sur un autre tenant garde le formulaire de CE tenant ; OMV : inchangé).
  const { isFeatureExact, isFeatureGranted } = usePermissions();
  const ficheCourte = isFeatureExact("commercial.prospect_fiche_courte");
  const [commerciaux, setCommerciaux] = useState(null);       // sélecteur « commercial attribué » (administrateur seulement)
  const moi = (() => { try { const u = JSON.parse(localStorage.getItem("user") || "{}") || {}; return u.name || u.email || null; } catch { return null; } })();
  // T-203 — suivi des actions : capacité lue en mode STRICT (le serveur refuse aussi sans elle) ; l'assignation à un autre est réservée aux gestionnaires.
  const crmActif = isFeatureGranted("commercial.crm_actions");
  const peutAssignerCrm = ["admin", "manager", "super_admin"].includes(role);
  // UNE seule vue formulaire, deux modes : create (vide) / edit (pré-rempli data{}).
  // Le rendu = OmvCeeEditor (le composant du formulaire DOSSIER).
  const [editor, setEditor] = useState({ open: false, mode: "create", prospect: null });
  // Dialogue « Créer / Utiliser le prospect » — ne s'ouvre QUE sur un vrai doublon
  // de FORMULAIRE (prospect même SIRET, monde prospect). cf. handleProspectSave.
  const [prospectDup, setProspectDup] = useState({ open: false, existing: null, pendingData: null });
  // 🔴 ETI-82 §C — suivi READ-ONLY du dossier d'un prospect (modale commercial).
  const [suivi, setSuivi] = useState({ open: false, prospect: null });

  const openCreate = () => {
    setEditor({ open: true, mode: "create", prospect: null });
    if (ficheCourte && !isRegie && commerciaux === null) {          // l'administrateur choisit le commercial ; le commercial de la régie est lui-même
      fetch(`${API}/api/regie/admin/commerciaux`, { headers: headers(), credentials: "include" })
        .then((r) => (r.ok ? r.json() : { commerciaux: [] })).then((d) => setCommerciaux(d.commerciaux || [])).catch(() => setCommerciaux([]));
    }
  };
  const openEdit = (prospect) => setEditor({ open: true, mode: "edit", prospect });
  const closeEditor = () => setEditor({ open: false, mode: "create", prospect: null });

  const headers = useCallback(() => {
    const tok = localStorage.getItem("session_token");
    return {
      "Content-Type": "application/json",
      ...enteteTenant(tenantId),
      ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
    };
  }, [tenantId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/api/metier/prospects${showArchived ? "?include_archived=true" : ""}`;
      const res = await fetch(url, { headers: headers(), credentials: "include" });
      if (res.ok) setData(await res.json());
      else toast.error("Erreur chargement prospects");
    } catch { toast.error("Erreur réseau"); }
    finally { setLoading(false); }
  }, [headers, showArchived]);

  useEffect(() => { load(); }, [load]);

  // T-203 — un clic dans « À faire » ouvre la fiche du prospect visé (quand la liste est chargée).
  useEffect(() => {
    if (!ouvrirProspectId || loading) return;
    const cible = (data.prospects || []).find((p) => p.id === ouvrirProspectId);
    if (cible) setEditor({ open: true, mode: "edit", prospect: cible });
    onProspectOuvert?.();
  }, [ouvrirProspectId, loading, data.prospects]);          // eslint-disable-line react-hooks/exhaustive-deps

  // Lignes : champs dérivés du data{} CEE du prospect (générique : le profil pilote
  // le schéma). On garde le prospect complet (_raw) pour le détail (clic ligne).
  const rows = useMemo(() => (data.prospects || []).map((p) => {
    const ent = (p.data && p.data.entreprise) || {};
    const bat = (p.data && p.data.batiment) || {};
    const cee = (p.data && p.data.cee) || {};
    return {
      id: p.id,
      beneficiaire: ent.raison_sociale || ent.siret || "—",
      site: bat.nom_etablissement || "—",
      cp: bat.cp_travaux || ent.cp || "—",           // T-201 : la fiche courte porte le code postal de l'entreprise
      ville: bat.ville_travaux || "—",
      operation: cee.operation_cee || cee.code_fiche || "—",
      source: p.source || "—",
      commercial_id: p.commercial_id || null,
      commercial: (p.commercial_id && (data.commerciaux || {})[p.commercial_id]) || (p.commercial_id ? "Commercial inconnu" : "—"),   // T-202
      created_at: p.created_at,
      statut: p.statut,
      statut_libelle: STATUT_LABELS[p.statut] || p.statut,
      archived: !!p.archived,
      siret: p.siret || ent.siret || "",   // conservé pour la recherche
      _raw: p,
    };
  }), [data.prospects, data.commerciaux]);

  // 9 colonnes (8 d'origine, ordre figé, demande JJ + « Commercial » T-202) :
  // Bénéficiaire | Site | CP | Ville | Opération | Commercial | Source | Reçu le | Statut
  const columns = useMemo(() => [
    { key: "beneficiaire", label: "Bénéficiaire", sortable: true },
    { key: "site", label: "Site", sortable: true },
    { key: "cp", label: "CP", sortable: true },
    { key: "ville", label: "Ville", sortable: true },
    { key: "operation", label: "Opération", sortable: true },
    // T-202 — filtre « commercial » (valeurs des lignes chargées) et filtre « statut » (tous les états, VH1 compris) : filtres par colonne d'OHGrid.
    { key: "commercial", label: "Commercial", sortable: true, filter: { type: "select", placeholder: "Tous", options: optionsCommerciaux(rows) } },
    { key: "source", label: "Source", sortable: true, render: (v) => sourceLabel(v) },
    { key: "created_at", label: "Reçu le", sortable: true, render: (v) => formatDate(v) },
    { key: "statut", label: "Statut", sortable: true, render: (v) => <StatutBadge value={v} />,
      filter: { type: "select", placeholder: "Tous", options: Object.entries(STATUT_LABELS).map(([value, label]) => ({ value, label })) } },
  ], [rows]);

  const counts = data.counts_by_statut || {};
  const stats = useMemo(() => [
    { key: "all", label: "Total", value: data.total || 0, color: "blue", filter: null },
    { key: "nouveau", label: "Nouveaux", value: counts.nouveau || 0, color: "green", filter: (r) => r.statut === "nouveau" },
    // T-202 — pipeline : l'état VH1 « validée » (statut en_revue) a sa pastille dès qu'un prospect y est ; « rejetés » porte aussi les refus VH1.
    ...(counts.en_revue ? [{ key: "en_revue", label: "En revue (VH1 validée)", value: counts.en_revue, color: "amber", filter: (r) => r.statut === "en_revue" }] : []),
    { key: "converti", label: "Convertis", value: counts.converti || 0, color: "cyan", filter: (r) => r.statut === "converti" },
    { key: "doublon", label: "Doublons", value: counts.doublon || 0, color: "orange", filter: (r) => r.statut === "doublon" },
    { key: "rejete", label: "Rejetés", value: counts.rejete || 0, color: "red", filter: (r) => r.statut === "rejete" },
    ...(counts.transmis ? [{ key: "transmis", label: "Transmis", value: counts.transmis, color: "purple", filter: (r) => r.statut === "transmis" }] : []),
    { key: "archive", label: "Archivés", value: data.archived_count || 0, color: "amber", filter: (r) => r.archived },
  ], [data.total, data.archived_count, counts]);

  // Archivage = geste PAR DÉFAUT (réversible) ; la suppression dure reste séparée.
  const archiveProspect = async (id, archived) => {
    try {
      const res = await fetch(`${API}/api/metier/prospects/${id}/${archived ? "archiver" : "desarchiver"}`, {
        method: "POST", headers: headers(), credentials: "include",
      });
      if (res.ok) { toast.success(archived ? "Prospect archivé" : "Prospect désarchivé"); closeEditor(); load(); }
      else toast.error("Erreur archivage");
    } catch { toast.error("Erreur réseau"); }
  };

  const setStatut = async (id, statut) => {
    try {
      const res = await fetch(`${API}/api/metier/prospects/${id}`, {
        method: "PATCH", headers: headers(), credentials: "include",
        body: JSON.stringify({ statut }),
      });
      if (res.ok) { toast.success(`Statut → ${STATUT_LABELS[statut] || statut}`); closeEditor(); load(); }
      else toast.error("Erreur statut");
    } catch { toast.error("Erreur réseau"); }
  };

  const removeProspect = async (id) => {
    if (!window.confirm("Supprimer ce prospect ?")) return;
    try {
      const res = await fetch(`${API}/api/metier/prospects/${id}`, { method: "DELETE", headers: headers(), credentials: "include" });
      if (res.ok) { toast.success("Prospect supprimé"); closeEditor(); load(); }
      else toast.error("Erreur suppression");
    } catch { toast.error("Erreur réseau"); }
  };

  // Cycle de vie UI simplifié (retrait "en revue") : écarter le junk (doublon /
  // rejeter / supprimer). Le vrai geste = CONVERTIR (action groupée, LOT 2).
  // RÉGIE : aucune action de cycle de vie (consultation seule de ses saisies ;
  // ces endpoints sont de toute façon 403 côté backend pour role=regie).
  const rowActions = isRegie ? [
    // RÉGIE : aucune action de cycle de vie ; seulement le SUIVI read-only du dossier.
    { label: "Suivi du dossier", icon: Eye, onClick: (row) => setSuivi({ open: true, prospect: row._raw }) },
  ] : [
    { label: "Marquer doublon", icon: Copy, onClick: (row) => setStatut(row.id, "doublon") },
    { label: "Rejeter", icon: XCircle, onClick: (row) => setStatut(row.id, "rejete") },
    // Geste PAR DÉFAUT = archiver (réversible). Désarchiver si déjà archivé.
    { label: "Archiver", icon: Archive, show: (row) => !row.archived, onClick: (row) => archiveProspect(row.id, true) },
    { label: "Désarchiver", icon: ArchiveRestore, show: (row) => row.archived, onClick: (row) => archiveProspect(row.id, false) },
    { label: "Supprimer (définitif)", icon: Trash2, className: "text-[var(--oh-error-text)]", onClick: (row) => removeProspect(row.id) },
  ];

  // Action groupée : conversion en lot (DOSSIER seul, pas de devis). Atomique par
  // item côté back ; rapport par item (converti / doublon SIRET rattaché / erreur)
  // + avertissements SIREN-soft. Le devis est généré ENSUITE manuellement.
  const convertSelection = async (ids) => {
    if (!ids || !ids.length) return;
    if (!window.confirm(`Convertir ${ids.length} prospect(s) en dossier(s) ? (sans devis — il sera généré ensuite)`)) return;
    try {
      const res = await fetch(`${API}/api/metier/prospects/convert`, {
        method: "POST", headers: headers(), credentials: "include",
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) { toast.error("Erreur conversion"); return; }
      const d = await res.json();
      const s = d.summary || {};
      const parts = [];
      if (s.converti) parts.push(`${s.converti} converti(s)`);
      if (s.rattaches) parts.push(`${s.rattaches} client(s) rattaché(s) (SIRET existant)`);
      if (s.deja_converti) parts.push(`${s.deja_converti} déjà converti(s)`);
      if (s.erreur) parts.push(`${s.erreur} erreur(s)`);
      (s.erreur ? toast.warning : toast.success)(parts.join(" · ") || "Conversion terminée");
      const warns = (d.results || []).filter((r) => r.siren_warning);
      if (warns.length) toast.info(`${warns.length} avertissement(s) SIREN : établissement(s) du même groupe déjà en base.`);
      load();
    } catch { toast.error("Erreur réseau"); }
  };

  // RÉGIE : pas de conversion (réservée à Solène ; 403 backend).
  // T-202 — gestes groupés : exporter (toute personne, ses propres lignes), attribuer et archiver (gestionnaires), convertir (gestionnaires).
  const exporterSelection = (ids) => {
    const choisies = rows.filter((r) => (ids || []).includes(r.id));
    if (!choisies.length) return;
    telechargerCsv(choisies, `prospects-${new Date().toISOString().slice(0, 10)}.csv`);
    toast.success(`${choisies.length} prospect(s) exporté(s)`);
  };
  const archiverSelection = async (ids) => {
    if (!ids || !ids.length) return;
    if (!window.confirm(`Archiver ${ids.length} prospect(s) ? (réversible)`)) return;
    try {
      const res = await fetch(`${API}/api/metier/prospects/archiver-lot`, { method: "POST", headers: headers(), credentials: "include", body: JSON.stringify({ ids, archived: true }) });
      if (!res.ok) { toast.error("Erreur archivage groupé"); return; }
      const d = await res.json(); toast.success(resumeLot(d.summary)); load();
    } catch { toast.error("Erreur réseau"); }
  };
  const [attribution, setAttribution] = useState({ open: false, ids: [], commercial_id: "", liste: [] });
  const ouvrirAttribution = async (ids) => {
    if (!ids || !ids.length) return;
    let liste = [];
    try {
      const r = await fetch(`${API}/api/regie/admin/commerciaux`, { headers: headers(), credentials: "include" });
      if (r.ok) liste = ((await r.json()).commerciaux || []).map((c) => ({ id: c.user_id, nom: c.name || c.email }));
    } catch { /* repli ci-dessous */ }
    if (!liste.length) liste = Object.entries(data.commerciaux || {}).map(([id, nom]) => ({ id, nom }));      // repli : ceux déjà vus dans la liste
    setAttribution({ open: true, ids, commercial_id: "", liste });
  };
  const confirmerAttribution = async () => {
    try {
      const res = await fetch(`${API}/api/metier/prospects/attribuer`, { method: "POST", headers: headers(), credentials: "include",
        body: JSON.stringify({ ids: attribution.ids, commercial_id: attribution.commercial_id || null }) });
      if (!res.ok) { toast.error("Erreur d'attribution"); return; }
      const d = await res.json(); toast.success(resumeLot(d.summary));
      setAttribution({ open: false, ids: [], commercial_id: "", liste: [] }); load();
    } catch { toast.error("Erreur réseau"); }
  };
  const bulkActions = isRegie ? [
    { label: "Exporter (CSV)", icon: Download, onClick: exporterSelection },
  ] : [
    { label: "Attribuer à un commercial", icon: UserCheck, onClick: ouvrirAttribution },
    { label: "Archiver", icon: Archive, onClick: archiverSelection },
    { label: "Exporter (CSV)", icon: Download, onClick: exporterSelection },
    { label: "Convertir en dossiers", icon: Inbox, onClick: convertSelection },
  ];

  // Dédup NON bloquante à la saisie (régie OU Solène) : booléen + réf masquée via
  // l'endpoint dédié (pas de fuite client/dossier). Passé à OmvCeeEditor.
  const checkDuplicate = useCallback(async (digits) => {
    const d = (digits || "").replace(/\D/g, "");
    const qs = d.length === 14 ? `siret=${d}` : `siren=${d}`;
    try {
      const res = await fetch(`${API}/api/metier/prospects/check-duplicate?${qs}`, { headers: headers(), credentials: "include" });
      if (res.ok) return await res.json();
    } catch { /* non bloquant */ }
    return null;
  }, [headers]);

  // Soumission UNIFIÉE (callback `onSubmit` d'OmvCeeEditor) : reçoit le `body`
  // {entreprise,representant,batiment,technique,cee} construit par LE composant
  // dossier → data{} == payload dossier (conversion lossless). create → POST ;
  // edit → PATCH. Retourne un booléen de succès au composant.
  // Persistance réelle (POST création / PATCH édition). Renvoie un booléen succès.
  const persistProspect = async (data, mode, prospect, file = null, options = {}) => {
    try {
      let res;
      if (mode === "edit" && prospect) {
        res = await fetch(`${API}/api/metier/prospects/${prospect.id}`, {
          method: "PATCH", headers: headers(), credentials: "include",
          body: JSON.stringify({ data }),
        });
      } else {
        // Source = "regie" pour le compte régie (sinon sa saisie ne rentrerait pas
        // dans son propre filtre de liste source=regie) ; "manuel" pour les internes.
        res = await fetch(`${API}/api/metier/prospects`, {
          method: "POST", headers: headers(), credentials: "include",
          body: JSON.stringify({ source: isRegie ? "regie" : "manuel", data, ...(options.commercial_id ? { commercial_id: options.commercial_id } : {}) }),
        });
      }
      if (res.ok) {
        // Attache le formulaire importé AU prospect (storage only, PAS Zoho — la
        // conversion poussera). Best-effort : un échec n'invalide pas la sauvegarde.
        if (file) {
          const saved = await res.json().catch(() => ({}));
          const prospectId = (mode === "edit" && prospect) ? prospect.id : saved?.prospect?.id;
          if (prospectId) {
            try {
              const tok = localStorage.getItem("session_token");
              const fd = new FormData();
              fd.append("file", file);
              await fetch(`${API}/api/metier/prospects/${prospectId}/attach-form`, {
                method: "POST", credentials: "include",
                // Pas de Content-Type : le navigateur pose le boundary multipart.
                headers: {
                  ...enteteTenant(tenantId || localStorage.getItem("current_tenant_slug")),
                  ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
                },
                body: fd,
              });
            } catch { /* attache best-effort — non bloquant */ }
          }
        }
        toast.success(mode === "edit" ? "Prospect mis à jour" : "Prospect créé");
        closeEditor();
        load();
        return true;
      }
      toast.error("Erreur enregistrement");
      return false;
    } catch { toast.error("Erreur réseau"); return false; }
  };

  // Fiche prospect (doc brut) → carte pour ClientDuplicateDialog (forme "dossier").
  const prospectToMatch = (p) => ({
    id: p.id,
    reference: p?.data?.entreprise?.raison_sociale || `Prospect ${String(p.id || "").slice(0, 8)}`,
    etablissement: p?.data?.batiment?.nom_etablissement || "",
    activite: p?.data?.entreprise?.secteur_activite || p.source || "",
    statut: STATUT_LABELS[p.statut] || p.statut || "nouveau",
    created_at: p.created_at,
    is_active: false,
  });

  // Soumission UNIFIÉE (callback `onSubmit` d'OmvCeeEditor). DÉDUP SÉPARÉE PAR
  // MONDE (règle JJ) : à la CRÉATION, si un PROSPECT de même SIRET existe déjà
  // (monde formulaire), on propose « Créer / Utiliser le prospect » (même
  // dialogue que le monde dossier). Un simple dossier/client existant N'EST PAS
  // un doublon de formulaire → aucun dialogue ici (juste l'info discrète inline).
  const handleProspectSave = async (body, file = null, options = {}) => {
    const data = {};
    for (const k of ["entreprise", "representant", "batiment", "technique", "cee"]) data[k] = body[k] || {};
    if (data.entreprise.siret) data.entreprise.siret = String(data.entreprise.siret).replace(/\D/g, "");

    if (editor.mode !== "edit") {
      const siret = data.entreprise.siret || "";
      if (siret.length === 14) {
        const dup = await checkDuplicate(siret);
        // SIRET-exact uniquement : un match SIREN-soft = autre établissement du
        // même groupe, PAS un doublon de formulaire → on laisse créer.
        if (dup?.prospect?.exists && dup.prospect.match === "siret" && dup.prospect.id) {
          try {
            const r = await fetch(`${API}/api/metier/prospects/${encodeURIComponent(dup.prospect.id)}`,
              { headers: headers(), credentials: "include" });
            const existing = r.ok ? await r.json() : null;
            if (existing) {
              setProspectDup({ open: true, existing, pendingData: data, pendingOptions: options });
              return false; // le dialogue prend le relais (pas d'erreur affichée)
            }
          } catch { /* fiche introuvable → on laisse la création se faire */ }
        }
      }
    }
    return persistProspect(data, editor.mode, editor.prospect, file, options);
  };

  return (
    <div className="space-y-4" data-testid="prospects-staging-grid">
      {/* Header aligné HubGrid : barre couleur + titre + badge total + action */}
      <div className="flex items-center gap-3">
        <div className="w-1 h-6 rounded-full" style={{ background: "var(--oh-stat-green-text)" }} />
        <h2 className="text-lg font-medium" style={{ color: "var(--oh-text)" }}>Prospects</h2>
        <Badge className="text-xs px-2 py-0.5"
          style={{ background: "var(--oh-stat-green-bg)", color: "var(--oh-stat-green-text)", border: "1px solid var(--oh-stat-green-border)" }}>
          {data.total || 0} prospect{(data.total || 0) > 1 ? "s" : ""}
        </Badge>
        <span className="text-xs hidden md:inline" style={{ color: "var(--oh-text-muted)" }}>
          Prospects à convertir en dossiers
        </span>
        {/* Bouton création TOUJOURS visible (échafaudage test ; le bouton OHGrid
            disparaît en mode sélection multiple → on garde celui-ci en repli sûr). */}
        <label className="ml-auto flex items-center gap-1.5 text-xs cursor-pointer" style={{ color: "var(--oh-text-muted)" }}>
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)}
            className="w-3.5 h-3.5 rounded" data-testid="prospects-show-archived" />
          Inclure archivés{data.archived_count ? ` (${data.archived_count})` : ""}
        </label>
        <Button size="sm" onClick={openCreate} data-testid="prospects-new-btn">
          <Plus className="w-4 h-4 mr-1" /> Nouveau prospect
        </Button>
      </div>

      {/* 🔴 ETI-82 — le commercial (régie) retrouve et copie SON lien de soumission. */}
      {isRegie && <MonLienCommercial tenantId={tenantId} />}

      <OHGrid
        data={rows}
        columns={columns}
        stats={stats}
        rowActions={rowActions}
        bulkActions={bulkActions}
        headerActions={[
          // Échafaudage de TEST (vrai flux = formulaire public LOT 3). Gatable.
          { label: "Nouveau prospect", icon: Plus, onClick: openCreate },
        ]}
        selectable={true}
        pagination={true}
        pageSize={25}
        loading={loading}
        onRefresh={load}
        onRowClick={(row) => openEdit(row._raw)}
        searchPlaceholder="Rechercher par bénéficiaire, établissement, SIRET..."
        searchFilter={(row, q) =>
          (row.beneficiaire || "").toLowerCase().includes(q) ||
          (row.site || "").toLowerCase().includes(q) ||
          (row.siret || "").includes(q)
        }
        defaultSortKey="created_at"
        defaultSortDir="desc"
        emptyMessage="Aucun prospect pour le moment"
        testIdPrefix="prospects"
      />

      {/* FICHE FORMULAIRE = LE composant du formulaire DOSSIER (OmvCeeEditor) :
          mêmes champs, mêmes dropdowns config-list (secteurs/fiches/délégataires/
          type_batiment/type_vmc/zone), même structure 2 parties. 2 modes :
          create = vide ; edit (clic ligne) = pré-rempli depuis prospect.data{}.
          La sauvegarde est déléguée via onSubmit → POST/PATCH /prospects (le
          composant ne PATCHe PAS un dossier). data{} == payload dossier (lossless).
          key = remonte le composant à chaque ouverture (ré-init de son état interne). */}
      <Dialog open={editor.open} onOpenChange={(o) => { if (!o) closeEditor(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <Building2 className="w-5 h-5" style={{ color: "var(--oh-text-muted)" }} />
              {editor.mode === "edit"
                ? (editor.prospect?.data?.entreprise?.raison_sociale || "Prospect")
                : (ficheCourte ? "Nouveau prospect" : "Nouveau prospect — Formulaire CEE")}
              {editor.mode === "edit" && editor.prospect && <StatutBadge value={editor.prospect.statut} />}
            </DialogTitle>
          </DialogHeader>
          {editor.mode === "edit" && editor.prospect ? (
            <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
              <Hash className="w-3 h-3 inline opacity-60" /> SIRET {editor.prospect.siret || "—"}
              {editor.prospect.siren ? ` (SIREN ${editor.prospect.siren})` : ""}
              {" • "}source {editor.prospect.source} • reçu le {formatDate(editor.prospect.created_at)}.{editor.prospect.statut === "transmis" ? " Prospect transmis : lecture seule." : " Champs éditables."}
            </p>
          ) : ficheCourte ? (
            <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>Fiche courte : l'essentiel pour ne pas perdre le contact. Le reste se complète ensuite.</p>
          ) : (
            <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
              Même formulaire que la fiche dossier (mêmes listes). Les prospects
              pourront aussi arriver par le formulaire web public.
            </p>
          )}

          {/* 🔴 ETI-101 — état + décision de la porte du PROSPECT, si le tenant en
              déclare une (sinon le composant ne rend RIEN). L'étape suivante est le
              geste EXISTANT (conversion), proposé une fois la porte validée — le
              serveur reste juge (garde `exiger_porte_franchie` sur /convert). */}
          {editor.mode === "edit" && editor.prospect && (
            <PorteDecision
              entiteType="prospect"
              entiteId={editor.prospect.id}
              tenantId={tenantId}
              role={role}
              actionSuivante={isRegie ? undefined : {
                label: "Convertir en dossier",
                onClick: () => convertSelection([editor.prospect.id]),
              }}
              onDecided={(r) => {
                if (r && r.prospect) setEditor((ed) => ({ ...ed, prospect: r.prospect }));
                load();
              }}
            />
          )}

          {/* ETI-115 L6 — envoi unitaire au partenaire / état « envoyé » + statut distant (le serveur reste juge). */}
          {editor.mode === "edit" && editor.prospect && (
            <EnvoiPartenaire
              prospect={editor.prospect}
              role={role}
              api={API}
              headers={headers}
              onSent={() => { closeEditor(); load(); }}
            />
          )}

          {crmActif && editor.mode === "edit" && editor.prospect && (
            <SuiviActions cibleType="prospect" cibleId={editor.prospect.id} tenantId={tenantId} peutAssigner={peutAssignerCrm} />
          )}

          {editor.open && editor.mode !== "edit" && ficheCourte && (
            <ProspectCreationRapide onSubmit={handleProspectSave} checkDuplicate={checkDuplicate} commerciaux={isRegie ? null : commerciaux} moi={moi} />
          )}

          {editor.open && !(editor.mode !== "edit" && ficheCourte) && (
            <fieldset disabled={editor.mode === "edit" && editor.prospect?.statut === "transmis"}
              data-testid="fiche-prospect-champs" style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
            <OmvCeeEditor
              key={editor.mode === "edit" ? (editor.prospect?.id || "edit") : "create"}
              dossier={editor.mode === "edit" ? (editor.prospect?.data || {}) : {}}
              tenantId={tenantId}
              onSubmit={handleProspectSave}
              submitLabel={editor.mode === "edit" ? "Enregistrer le prospect" : "Créer le prospect"}
              checkDuplicate={checkDuplicate}
              readOnly={editor.mode === "edit" && (isRegie || editor.prospect?.statut === "transmis")}
            />
            </fieldset>
          )}

          <DialogFooter className="gap-2 flex-wrap">
            {/* Cycle de vie (écarter le junk). La sauvegarde = bouton interne du formulaire.
                Masqué pour la régie (consultation seule de ses saisies). */}
            {!isRegie && editor.mode === "edit" && editor.prospect && (
              <div className="flex items-center gap-2 mr-auto">
                <Button variant="ghost" size="sm" onClick={() => setStatut(editor.prospect.id, "doublon")}>Doublon</Button>
                <Button variant="ghost" size="sm" onClick={() => setStatut(editor.prospect.id, "rejete")}>Rejeter</Button>
                <Button variant="outline" size="sm" className="text-[var(--oh-error-text)]" onClick={() => removeProspect(editor.prospect.id)}>Supprimer</Button>
              </div>
            )}
            <Button variant="ghost" onClick={closeEditor}>{editor.mode === "edit" ? "Fermer" : "Annuler"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* T-202 — attribution groupée à un commercial du tenant */}
      <Dialog open={attribution.open} onOpenChange={(o) => { if (!o) setAttribution({ open: false, ids: [], commercial_id: "", liste: [] }); }}>
        <DialogContent className="max-w-md" data-testid="prospects-attribution">
          <DialogHeader><DialogTitle>Attribuer {attribution.ids.length} prospect(s)</DialogTitle></DialogHeader>
          <label htmlFor="attribution-commercial" className="text-xs" style={{ color: "var(--oh-text-muted)" }}>Commercial</label>
          <select id="attribution-commercial" data-testid="attribution-commercial" value={attribution.commercial_id}
            onChange={(e) => setAttribution((a) => ({ ...a, commercial_id: e.target.value }))}
            style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)", color: "var(--oh-text)", borderRadius: 8, height: 36, padding: "0 12px", colorScheme: "light dark" }}>
            <option value="">Retirer l'attribution</option>
            {attribution.liste.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
          </select>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setAttribution({ open: false, ids: [], commercial_id: "", liste: [] })}>Annuler</Button>
            <Button onClick={confirmerAttribution} data-testid="attribution-confirmer">Attribuer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Doublon de FORMULAIRE (monde prospect) : même mécanisme « Créer / Utiliser »
          que le monde dossier, alimenté par le prospect existant. */}
      <ClientDuplicateDialog
        open={prospectDup.open}
        mode="dossier"
        entity="prospect"
        matches={prospectDup.existing ? [prospectToMatch(prospectDup.existing)] : []}
        title="Un prospect identique existe déjà"
        description="Une fiche prospect avec ce SIRET existe déjà pour ce compte. Créez une nouvelle fiche (homonymie / nouvelle demande) ou ouvrez l'existante."
        createLabel="Créer un nouveau prospect"
        useLabel="Utiliser le prospect existant"
        onCreateAnyway={() => {
          const d = prospectDup.pendingData;
          setProspectDup({ open: false, existing: null, pendingData: null });
          if (d) persistProspect(d, "create", null, null, prospectDup.pendingOptions || {});
        }}
        onUseExisting={() => {
          const existing = prospectDup.existing;
          setProspectDup({ open: false, existing: null, pendingData: null });
          if (existing) openEdit(existing); // ouvre la fiche prospect existante
        }}
        onCancel={() => setProspectDup({ open: false, existing: null, pendingData: null })}
      />

      {/* 🔴 ETI-82 §C — suivi READ-ONLY du dossier issu du prospect (commercial). */}
      <FicheProspectRegie
        open={suivi.open}
        prospect={suivi.prospect}
        tenantId={tenantId}
        onClose={() => setSuivi({ open: false, prospect: null })}
      />
    </div>
  );
}
